import React, { useState } from 'react';
import DesignCard from './DesignCard';
import AdBanner from './AdBanner';
import AdGalleryBanner from './AdGalleryBanner';
import { Search, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Mock gallery data
const GALLERY_ITEMS = [
  {
    id: 1,
    imageUrl: '/images/tattobutterfly.png',
    title: 'Minimalist Wolf',
    category: 'Tattoo',
    type: 'tattoo'
  },
  {
    id: 2,
    imageUrl: '/images/nails1.png',
    title: 'Pastel Gradient',
    category: 'Nail Art',
    type: 'nail'
  },
  {
    id: 3,
    imageUrl: '/images/tattoleon.png',
    title: 'Geometric Lion',
    category: 'Tattoo',
    type: 'tattoo'
  },
  {
    id: 4,
    imageUrl: '/images/nails2.png',
    title: 'French Tips',
    category: 'Nail Art',
    type: 'nail'
  },
  {
    id: 5,
    imageUrl: '/images/tattohand.png',
    title: 'Watercolor Abstract',
    category: 'Tattoo',
    type: 'tattoo'
  },
  {
    id: 6,
    imageUrl: '/images/nails3.png',
    title: 'Galaxy Theme',
    category: 'Nail Art',
    type: 'nail'
  },
  {
    id: 7,
    imageUrl: '/images/tatoo eye.png',
    title: 'Mandala Flower',
    category: 'Tattoo',
    type: 'tattoo'
  },
  {
    id: 8,
    imageUrl: '/images/nails4.png',
    title: 'Metallic Accent',
    category: 'Nail Art',
    type: 'nail'
  },
];

const Gallery: React.FC<{ selectedType?: 'all' | 'tattoo' | 'nail' }> = ({ 
  selectedType = 'all' 
}) => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'tattoo' | 'nail'>(selectedType);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useLanguage();

  const filteredItems = GALLERY_ITEMS.filter(item => {
    const matchesType = activeFilter === 'all' || item.type === activeFilter;
    const matchesSearch = !searchQuery || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesType && matchesSearch;
  });

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <h2 className="text-3xl font-bold">{t('gallery.title')}</h2>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('gallery.search')}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 w-full sm:w-64 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          </div>
          
          <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg p-1">
            <button
              className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                activeFilter === 'all' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'
              }`}
              onClick={() => setActiveFilter('all')}
            >
              {t('gallery.filters.all')}
            </button>
            <button
              className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                activeFilter === 'tattoo' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'
              }`}
              onClick={() => setActiveFilter('tattoo')}
            >
              {t('gallery.filters.tattoos')}
            </button>
            <button
              className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                activeFilter === 'nail' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'
              }`}
              onClick={() => setActiveFilter('nail')}
            >
              {t('gallery.filters.nailArt')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Custom Ad Banner for Gallery */}
      <div className="mb-8">
        <AdGalleryBanner />
      </div>
      
      {/* Gallery Grid */}
      {filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredItems.map((item) => (
            <DesignCard
              key={item.id}
              imageUrl={item.imageUrl}
              title={item.title}
              category={item.category}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
          <p className="text-gray-400 text-lg mb-2">{t('gallery.noResults.title')}</p>
          <p className="text-gray-500">{t('gallery.noResults.subtitle')}</p>
        </div>
      )}
    </div>
  );
};

export default Gallery;